import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssetsLibrarySong } from '../../../../../../music24-api/src/kit/assetsLibrary/assetsLibraryTypes';
import { useTRPCAPI } from '../../../../trpc/trpc';
import { useRealtimeEvent } from '../../../realtime/useRealtimeEvent';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import { byNewest, byOldest, WorkEntry, workEntries } from './workListModel';

/** The poll while a file is made and the socket is down (owner rule: no poll while connected). */
export const WORK_POLL_MS = 15 * 1000;
/** The files that finished in the last days show under "Finished". */
export const WORK_DONE_DAYS = 7;
const WORK_LIST_LIMIT = 96;
const WORK_DONE_LIMIT = 24;

export interface WorkLists {
  /** Queued and made now, the oldest first. */
  busy: WorkEntry[];
  /** The open failures (the last 7 days, not tried again), the newest first. */
  failed: WorkEntry[];
  /** The files that finished in the last 7 days, the newest first. Empty with `withDone: false`. */
  done: WorkEntry[];
  songs: Record<string, AssetsLibrarySong>;
  isLoading: boolean;
  /** Set when the busy list or the failed list has no answer. */
  error: unknown;
  refetch: () => void;
}

/**
 * The lists of the Work page from `assets.list` (the route of the old Assets page; no new API
 * route). One read for each state: `working` (queued too), `failed` (the open failures) and
 * `ready` of the last 7 days. The kit asset and reel events read them again; the poll runs only
 * while something is made and the socket is down.
 */
export function useWorkLists(input: {
  accountId: number | null;
  catalogItemUuid?: string;
  /** False skips the finished files: the redirect of a link needs only the work in progress. */
  withDone?: boolean;
  enabled?: boolean;
}): WorkLists {
  const trpcAPI = useTRPCAPI();
  const queryClient = useQueryClient();
  const pollMs = useRealtimePollInterval(WORK_POLL_MS);
  const enabled = (input.enabled ?? true) && input.accountId !== null;
  const accountId = input.accountId ?? 0;
  const catalogItemUuid = input.catalogItemUuid ?? null;

  const busyQuery = useQuery({
    ...trpcAPI.assets.list.queryOptions(
      { accountId, catalogItemUuid, state: 'working', limit: WORK_LIST_LIMIT },
      { enabled, retry: false }
    ),
    refetchInterval: (state) => (pollMs > 0 && (state.state.data?.items.length ?? 0) > 0 ? pollMs : false),
  });
  const failedQuery = useQuery(
    trpcAPI.assets.list.queryOptions(
      { accountId, catalogItemUuid, state: 'failed', limit: WORK_LIST_LIMIT },
      { enabled, retry: false }
    )
  );
  const doneQuery = useQuery(
    trpcAPI.assets.list.queryOptions(
      { accountId, catalogItemUuid, state: 'ready', sinceDays: WORK_DONE_DAYS, limit: WORK_DONE_LIMIT },
      { enabled: enabled && (input.withDone ?? true), retry: false }
    )
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: trpcAPI.assets.list.pathKey() }).catch(console.error);
  }, [queryClient, trpcAPI]);
  useRealtimeEvent('kit.asset.updated', refresh);
  useRealtimeEvent('reel.updated', refresh);

  const busy = useMemo(() => byOldest(workEntries(busyQuery.data?.items)), [busyQuery.data]);
  const failed = useMemo(() => byNewest(workEntries(failedQuery.data?.items)), [failedQuery.data]);
  const done = useMemo(() => byNewest(workEntries(doneQuery.data?.items)), [doneQuery.data]);
  const songs = useMemo(
    () => ({ ...doneQuery.data?.songs, ...failedQuery.data?.songs, ...busyQuery.data?.songs }),
    [busyQuery.data, doneQuery.data, failedQuery.data]
  );

  // A file that leaves the busy list (the poll saw it) is now finished or failed: read all again.
  const busyKeys = useMemo(() => busy.map((entry) => entry.key).join(','), [busy]);
  const lastBusyKeys = useRef(busyKeys);
  useEffect(() => {
    if (lastBusyKeys.current !== busyKeys) {
      lastBusyKeys.current = busyKeys;
      refresh();
    }
  }, [busyKeys, refresh]);

  const hasAnswer = busyQuery.data !== undefined && failedQuery.data !== undefined;
  return {
    busy,
    failed,
    done,
    songs,
    isLoading: busyQuery.isLoading || failedQuery.isLoading || doneQuery.isLoading,
    error: hasAnswer ? undefined : (busyQuery.error ?? failedQuery.error ?? undefined),
    refetch: refresh,
  };
}
